import { useSelector } from "react-redux";
import { scrollToId } from "./utils/scrollToId";
import "./App.scss";
import { lazy, Suspense, useEffect, useState } from "react";
import i18n from "./plugins/i18n";
import LazyOnVisible from "./components/LazyOnVisible";
import { useIdleMount } from "./hooks/useIdleMount";

import Index from "./components/Index";

const Chatbot = lazy(() => import("./components/Chatbot"));
const Tech = lazy(() => import("./components/Tech"));
const Portfolio = lazy(() => import("./components/Portfolio"));
const ItJobs = lazy(() => import("./components/ItJobs"));
const Footer = lazy(() => import("./components/Footer"));
const GithubStatus = lazy(() => import("./components/GithubStatus"));

const SUPPORTED_LANGUAGES = ["en", "ja"];
const resolveInitialLanguage = (): string => {
  try {
    const saved = localStorage.getItem("language");
    if (saved && SUPPORTED_LANGUAGES.includes(saved)) return saved;
  } catch {
    void 0;
  }
  const browserLang = navigator.language.slice(0, 2);
  return SUPPORTED_LANGUAGES.includes(browserLang) ? browserLang : "en";
};
i18n.changeLanguage(resolveInitialLanguage());

const weatherCodeToClass = (code: number): string => {
  switch (code) {
    case 0: // Clear sky
    case 1: // Mainly clear
      return "sunny";
    case 2: // Partly cloudy
    case 3: // Overcast
      return "bright";
    case 45: // Fog
    case 48: // Depositing rime fog
      return "cloudy";
    case 51: // Light drizzle
    case 53: // Moderate drizzle
    case 55: // Dense drizzle
    case 61: // Slight rain
    case 63: // Moderate rain
    case 65: // Heavy rain
    case 80: // Rain showers slight
    case 81: // Rain showers moderate
    case 82: // Rain showers violent
      return "rainy";
    case 66: // Freezing rain light
    case 67: // Freezing rain heavy
      return "heavy-rain";
    case 71: // Slight snow fall
    case 73: // Moderate snow fall
    case 75: // Heavy snow fall
    case 77: // Snow grains
    case 85: // Snow showers slight
    case 86: // Snow showers heavy
      return "snow";
    case 95: // Thunderstorm
    case 96: // Thunderstorm with slight hail
    case 99: // Thunderstorm with heavy hail
      return "heavy-rain"; // or you could create "thunder" class
    default:
      return "sunny"; // fallback
  }
};

function App() {

  const darkMode = useSelector(
    (state: { theme: { darkMode: boolean } }) => state.theme.darkMode,
  );
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [weather, setWeather] = useState<Record<string, string>>();

  const chatbotReady = useIdleMount(4000);

  useEffect(() => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);

    (async () => {
      try {
        const res = await fetch(
          "https://ipinfo.io/json?token=2363a54cb48a39",
          { signal: controller.signal },
        );
        const data = await res.json();
        const [lat, lon] = data.loc.split(",");
        setCity(data.city);
        setCountry(data.country || "UNKNOWN");

        const weatherRes = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current_weather=true&timezone=auto`,
          { signal: controller.signal },
        );
        const weatherData = await weatherRes.json();

        setWeather({
          temp: weatherData?.current_weather?.temperature,
          isDay: weatherData?.current_weather?.is_day,
          weather: weatherCodeToClass(weatherData?.current_weather?.weathercode),
        });
      } catch {
        setCountry((current) => current || "UNKNOWN");
        void 0;
      } finally {
        clearTimeout(timeoutId);
      }
    })();

    return () => controller.abort();
  }, []);

  return (
    <div className="App">
      {chatbotReady && (
        <Suspense fallback={null}>
          <Chatbot darkMode={darkMode} weather={weather} city={city} />
        </Suspense>
      )}
      <main>
        <Index
          weather={
            (weather && !weather?.isDay) ||
            (darkMode && weather?.weather === "sunny") ||
            (darkMode && weather?.weather === "bright")
              ? "night"
              : weather?.weather
          }
          country={country}
        ></Index>
        <LazyOnVisible rootMargin="800px 0px" minHeight={200}>
          <Suspense fallback={null}>
            <Tech></Tech>
          </Suspense>
        </LazyOnVisible>
        <div className={`wrapper ${darkMode ? "dark" : ""}`}>
          <LazyOnVisible rootMargin="800px 0px" minHeight={600}>
            <Suspense fallback={null}>
              <Portfolio></Portfolio>
            </Suspense>
          </LazyOnVisible>
          <LazyOnVisible rootMargin="800px 0px" minHeight={300}>
            <Suspense fallback={null}>
              <GithubStatus />
            </Suspense>
          </LazyOnVisible>
          <LazyOnVisible rootMargin="800px 0px" minHeight={300}>
            <Suspense fallback={null}>
              <ItJobs></ItJobs>
            </Suspense>
          </LazyOnVisible>
          <LazyOnVisible rootMargin="800px 0px" minHeight={200}>
            <Suspense fallback={null}>
              <Footer></Footer>
            </Suspense>
          </LazyOnVisible>
        </div>
      </main>

      <div className={`move-up ${darkMode ? "dark" : ""}`}>
        <a
          href="#home"
          aria-label="Back to top"
          onClick={(e) => {
            e.preventDefault();
            scrollToId("home");
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill={darkMode ? "white" : "#011627"}
            style={{ transform: "rotate(180deg)" }}
          >
            <path d="M12 17c-.3 0-.5-.1-.7-.3l-7-7a1 1 0 011.4-1.4L12 14.6l6.3-6.3a1 1 0 111.4 1.4l-7 7c-.2.2-.4.3-.7.3z" />
          </svg>
        </a>
      </div>
    </div>
  );
}

export default App;
